import { useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { LabelChangeForUpdate } from 'src/apps/web-sdks/services/api/LabelUpdateApiService';
import { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { buildDisplayNameToKeyMap, extractLabelKey } from '../../shared/helpers';
import {
	extractLabelOptionsFromTestRows,
	transformCoreLabelsToOptions
} from '../helpers/UpdateTestsModalHelpers';

interface UseLabelsManagementProps {
	testRows: IWebSdkTest[];
	tenantConfiguredLabels: ConfigurationLabel[];
}

interface UseLabelsManagementReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	selectedNewValue: OptionType[];
	setSelectedNewValue: (value: OptionType[]) => void;
	changes: LabelChangeForUpdate[];
	labelOptions: OptionType[];
	valueOptions: OptionType[];
	removeChange: (index: number) => void;
	resetChanges: () => void;
}

export const useLabelsManagement = ({
	testRows,
	tenantConfiguredLabels
}: UseLabelsManagementProps): UseLabelsManagementReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [selectedNewValue, setSelectedNewValue] = useState<OptionType[]>([]);
	const [changes, setChanges] = useState<LabelChangeForUpdate[]>([]);

	const labelOptions = useMemo((): OptionType[] => {
		return extractLabelOptionsFromTestRows(testRows, tenantConfiguredLabels);
	}, [testRows, tenantConfiguredLabels]);

	const valueOptions = useMemo((): OptionType[] => {
		const selectedLabelValue =
			selectedLabel.length > 0 ? (selectedLabel[0].value as string) : undefined;
		return transformCoreLabelsToOptions(tenantConfiguredLabels, selectedLabelValue);
	}, [tenantConfiguredLabels, selectedLabel]);

	useEffect(() => {
		if (selectedLabel.length > 0 && selectedNewValue.length > 0) {
			const oldLabelValue = selectedLabel[0].value as string;
			const newLabelValue = selectedNewValue[0].value as string;

			// Normalize the key from display name to original key
			const displayNameMap = buildDisplayNameToKeyMap(tenantConfiguredLabels);
			const normalizedKey = extractLabelKey(oldLabelValue, displayNameMap);

			const newChange: LabelChangeForUpdate = {
				oldLabelValue,
				newLabelValue: `${normalizedKey}:${newLabelValue}`
			};

			setChanges((prevChanges) => {
				const existingIndex = prevChanges.findIndex(
					(change) => change.oldLabelValue === newChange.oldLabelValue
				);
				if (existingIndex >= 0) {
					const updated = [...prevChanges];
					updated[existingIndex] = newChange;
					return updated;
				}
				return [...prevChanges, newChange];
			});

			setSelectedLabel([]);
			setSelectedNewValue([]);
		}
	}, [selectedLabel, selectedNewValue, tenantConfiguredLabels]);

	const removeChange = (index: number): void => {
		setChanges((prev) => prev.filter((_, i) => i !== index));
	};

	const resetChanges = (): void => {
		setChanges([]);
		setSelectedLabel([]);
		setSelectedNewValue([]);
	};

	return {
		selectedLabel,
		setSelectedLabel,
		selectedNewValue,
		setSelectedNewValue,
		changes,
		labelOptions,
		valueOptions,
		removeChange,
		resetChanges
	};
};
